import { Box, Button, Flex, HStack, Text, VStack, useColorModeValue } from "@chakra-ui/react";
import { useAppContext } from "../AppContext";

const MAXFONTSIZE = 40;
const MINFONTSIZE = 12;
const MAXLINEHEIGHT = 2.5;
const MINLINEHEIGHT = 1.5;
const LINEHEIGHTSTEP = 0.25;

interface LineIconProps {
  numLines: number;
}

const LineIcon: React.FC<LineIconProps> = ({ numLines }) => {
  const bgColor = useColorModeValue("black", "white");
  return (
    <VStack
      width="30px"
      height="30px"
      spacing={numLines === 1 ? "0" : `calc((30px - ${numLines * 8}px) / ${numLines + 1})`}
      align="center"
      justify="space-evenly"
    >
      {Array.from({ length: numLines }).map((_, index) => (
        <Box key={index} height="2px" width="100%" backgroundColor={bgColor} borderRadius="2px" />
      ))}
    </VStack>
  );
};

export const FontSizeAndLineHeight = (): JSX.Element => {
  const { textAreaFontSize, setTextAreaFontSize, textAreaLineHeight, setTextAreaLineHeight, isEnglish } =
    useAppContext();

  const handleIncreaseFontSize = () => {
    const newFontSize = Math.min(textAreaFontSize + 2, MAXFONTSIZE);
    setTextAreaFontSize(newFontSize);
    localStorage.setItem("textAreaFontSize", newFontSize.toString());
  };

  const handleDecreaseFontSize = () => {
    const newFontSize = Math.max(textAreaFontSize - 2, MINFONTSIZE);
    setTextAreaFontSize(newFontSize);
    localStorage.setItem("textAreaFontSize", newFontSize.toString());
  };

  const handleChangeLineHeight = () => {
    let newLineHeight = textAreaLineHeight + LINEHEIGHTSTEP;
    if (newLineHeight >= MAXLINEHEIGHT) {
      newLineHeight = MINLINEHEIGHT;
    }
    setTextAreaLineHeight(newLineHeight);
    localStorage.setItem("textAreaLineHeight", newLineHeight.toString());
  };

  const numOfLinesInBox = (): number => {
    return 5 - (textAreaLineHeight - MINLINEHEIGHT) / LINEHEIGHTSTEP;
  };

  const fontChar = isEnglish ? "A" : "字";
  return (
    <>
      {/* Font Sizes */}
      <Flex align="center" justify="space-between">
        <Text fontSize="lg" as="b">
          {isEnglish ? `Text Size:` : `字體大小：`}
        </Text>
        <Flex gap={"10px"}>
          <Button w="80px" onClick={handleDecreaseFontSize} disabled={textAreaFontSize <= MINFONTSIZE}>
            {fontChar}-
          </Button>
          <Box w={"20px"} h={"20px"} display="flex" justifyContent="center" alignItems={"center"}>
            <Text fontSize={textAreaFontSize} mt="20px">
              {fontChar}
            </Text>
          </Box>
          <Button w="80px" onClick={handleIncreaseFontSize} disabled={textAreaFontSize >= MAXFONTSIZE}>
            {fontChar}+
          </Button>
        </Flex>
      </Flex>

      {/* Line Heights */}
      <Flex align="center" justify="space-between">
        <Text fontSize="lg" as="b">
          {isEnglish ? `Line Heights:` : `行間距：`}
        </Text>
        <HStack w={"130px"} alignContent={"left"}>
          <Button onClick={handleChangeLineHeight}>
            <LineIcon numLines={numOfLinesInBox()} />
          </Button>
        </HStack>
      </Flex>
    </>
  );
};
